// src/components/tabs/Sectors.js
import React, { useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import { getSectorColor } from "../../config/colorScheme";
import { useSectorEmissionsTS } from "../../hooks/useSectorEmissionsWithFood";
import SectorEnergyVectorChart from "../dashboard/charts/SectorEnergyVectorChart";
import FoodSectorEnergyVectorChart from "../dashboard/charts/FoodSectorEnergyVectorChart";

// one place to control chart height (adjust to taste)
const PANEL_H = "h-[22rem] md:h-[24rem]";

// Simple: map UI sector key -> FE sector codes
function sectorCodesFor(sectorKey) {
  switch ((sectorKey || "").toLowerCase()) {
    case "buildings": return ["RES"];
    case "transport": return ["TRA"];
    case "industry":  return ["IND"];
    case "food":      return ["AGR"];
    default:          return ["AGR"];
  }
}

// scenario helpers to align comparison with the same family/suffix as parent key
const parseFamily = (key) => {
  const hasDVL = key?.includes("_dvl_");
  const base = hasDVL ? "net_zero_futures_dvl" : "net_zero_futures";
  const suffix = key?.match(/_(\d{4})$/)?.[0] ?? ""; // e.g. "_2208", "_0509"
  return { base, suffix };
};
const buildKey = (base, name, ambition, suffix) => `${base}_${name}_${ambition}${suffix}`;

function SectorSection({
  sectorKey,
  title,
  scenario,
  from,
  to,
  emisLabels,
  emisPrimaryData,
  emisComparisonData, // optional overlay
  comparisonLabel,    // optional label for overlay
  comparisonScenarioKey, // passed through to vector chart
}) {
  const datasets = useMemo(() => {
    const baseDs = [
      {
        label: "Emissions (Mt CO₂e)",
        data: emisPrimaryData,
        borderColor: getSectorColor(sectorKey, "primary"),
        backgroundColor: getSectorColor(sectorKey, "light"),
        fill: true,
        tension: 0.3,
        pointRadius: 0,
      },
    ];
    if (emisComparisonData) {
      baseDs.push({
        label: comparisonLabel ?? "Comparison",
        data: emisComparisonData,
        borderColor: getSectorColor(sectorKey, "primary"),
        backgroundColor: "transparent",
        borderDash: [6, 4],
        fill: false,
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2,
        // make overlay a touch darker by not using the light fill
      });
    }
    return baseDs;
  }, [emisPrimaryData, emisComparisonData, sectorKey, comparisonLabel]);

  const emisChart = useMemo(() => ({
    labels: emisLabels.map(String),
    datasets,
  }), [emisLabels, datasets]);

  return (
    <>
      <h4 className="text-lg font-medium text-gray-700 text-center mb-4 w-full clear-both block">
        {title}
      </h4>

      <div className="flex flex-col md:flex-row gap-4">
        {/* Emissions (from GraphQL) */}
        <div className="bg-gray-50 p-4 rounded-lg md:w-1/2">
          <div className={`relative ${PANEL_H}`}>
            <Line
              data={emisChart}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  title: { display: true, text: `${title} Emissions`, font: { size: 14 } },
                  legend: { display: true, labels: { boxWidth: 10 } },
                },
                scales: {
                  y: { title: { display: true, text: "Mt CO₂e" },min:0, beginAtZero: false },
                  x: { title: { display: true, text: "Year" } },
                },
              }}
            />
          </div>
        </div>

        {/* Energy Demand by Vector (Final Energy) */}
        <div className="bg-gray-50 p-4 rounded-lg md:w-1/2">
          <div className={`relative ${PANEL_H}`}>

{/* <SectorEnergyVectorChart
  scenario={scenario}
  comparisonScenarioKey={comparisonScenarioKey}
  sectorCodes={sectorCodesFor("buildings")}
  title="Buildings — Energy Demand by Source"
  from={from}
  to={to}
  profile="default"
  includeOther
  source="comset"
  showDebug
/> */}

  <SectorEnergyVectorChart
    scenario={scenario}
              comparisonScenarioKey={comparisonScenarioKey}
              sectorCodes={sectorCodesFor(sectorKey)}
              title={`${title} — Energy Demand by Source`}
              from={from}
              to={to}
              profile={
                sectorKey === "transport" ? "transport" :
                sectorKey === "industry"  ? "industry"  : "default"
              }
              includeOther={!(sectorKey === "transport" || sectorKey === "industry")}
              source={(sectorKey === "transport" || sectorKey === "industry" || sectorKey === "food" || sectorKey === "buildings") ? "proset" : "comset"}
  />


            {/* <SectorEnergyVectorChart
              scenario={scenario}
              comparisonScenarioKey={comparisonScenarioKey}
              sectorCodes={sectorCodesFor(sectorKey)}
              title={`${title} — Energy Demand by Source`}
              from={from}
              to={to}
              profile={
                sectorKey === "transport" ? "transport" :
                sectorKey === "industry"  ? "industry"  : "default"
              }
              includeOther={!(sectorKey === "transport" || sectorKey === "industry")}
              source={(sectorKey === "transport" || sectorKey === "industry" || sectorKey === "food" || sectorKey === "buildings") ? "proset" : "comset"}
            /> */}
          </div>
        </div>
      </div>
    </>
  );
}

export default function Sectors({
  scenarioKey,                     // primary scenario comes from parent
  from = 2025,
  to = 2050,
  // sectorImpact,
  // calculateIndividualSectorEmissionsReduction,
  // calculateSectorEnergyDemandByVector,
}) {
  // derive family/suffix from parent key for comparison mapping
  const { base, suffix } = useMemo(() => parseFamily(scenarioKey), [scenarioKey]);

  // local comparison state (dropdown value + computed backend key)
  const [selectedComparisonValue, setSelectedComparisonValue] = useState("");
  const comparisonScenarioKey = useMemo(() => {
    if (!selectedComparisonValue) return null;
    const [name, ambition = "low"] = selectedComparisonValue.split("-");
    return buildKey(base, name, ambition, suffix);
  }, [selectedComparisonValue, base, suffix]);

  // primary emissions
  const {
    labels: emisLabels,
    seriesBySector: seriesPrimary,
    loading,
    error,
  } = useSectorEmissionsTS({ scenario: scenarioKey, from, to, valueDivisor: 1000 });

  // comparison emissions (always call hook; only use if a comparison is chosen)
  const {
    seriesBySector: seriesCompare,
  } = useSectorEmissionsTS({ scenario: comparisonScenarioKey ?? scenarioKey, from, to, valueDivisor: 1000 });

  if (loading) return <div className="w-full h-96 grid place-items-center">Loading sector emissions…</div>;
  if (error)   return <div className="w-full h-96 text-red-600">Error: {error.message}</div>;

  const hasComparison = Boolean(comparisonScenarioKey);
  const comparisonLabel = hasComparison ? selectedComparisonValue.replace("-", " ").replace(/\b\w/g, c => c.toUpperCase()) : undefined;

  return (
    <div>
      <div className="bg-white p-6 rounded-lg mb-8 border border-gray-200">
        <h3 className="text-xl font-semibold text-gray-800 mb-6 text-center">Sector Impact Overview</h3>

        <div className="flex flex-col gap-10">
          <SectorSection
            sectorKey="food"
            title="Food"
            scenario={scenarioKey}
            comparisonScenarioKey={comparisonScenarioKey}
            from={from}
            to={to}
            emisLabels={emisLabels}
            emisPrimaryData={seriesPrimary.food}
            emisComparisonData={hasComparison ? seriesCompare.food : undefined}
            comparisonLabel={comparisonLabel}
          />
          <SectorSection
            sectorKey="buildings"
            title="Buildings & Homes"
            scenario={scenarioKey}
            comparisonScenarioKey={comparisonScenarioKey}
            from={from}
            to={to}
            emisLabels={emisLabels}
            emisPrimaryData={seriesPrimary.buildings}
            emisComparisonData={hasComparison ? seriesCompare.buildings : undefined}
            comparisonLabel={comparisonLabel}
          />
          <SectorSection
            sectorKey="transport"
            title="Transport"
            scenario={scenarioKey}
            comparisonScenarioKey={comparisonScenarioKey}
            from={from}
            to={to}
            emisLabels={emisLabels}
            emisPrimaryData={seriesPrimary.transport}
            emisComparisonData={hasComparison ? seriesCompare.transport : undefined}
            comparisonLabel={comparisonLabel}
          />
          <SectorSection
            sectorKey="industry"
            title="Industry"
            scenario={scenarioKey}
            comparisonScenarioKey={comparisonScenarioKey}
            from={from}
            to={to}
            emisLabels={emisLabels}
            emisPrimaryData={seriesPrimary.industry}
            emisComparisonData={hasComparison ? seriesCompare.industry : undefined}
            comparisonLabel={comparisonLabel}
          />
        </div>
      </div>
    </div>
  );
}
